import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminContext = createContext({
  isSuperAdmin: false,
  isModerator: false,
  podeEditarFaq: false,
  isLoading: true,
  subscriptionData: null,
  isPlanActive: false,
  isPlanExpired: false,
  refreshSubscription: () => {},
});

export const AdminProvider = ({ children }) => {
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [podeEditarFaq, setPodeEditarFaq] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionData, setSubscriptionData] = useState(null);

  const fetchAdminStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      // Buscar dados de assinatura (que incluem is_super_admin)
      const response = await axios.get(`${API}/auth/subscription`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      setSubscriptionData(response.data);
      setIsSuperAdmin(response.data.is_super_admin || false);
      // Papel MODERADOR (super admin > moderador > usuários): acessa a gestão de
      // usuários do painel e edita a Central de Ajuda.
      setIsModerator(response.data.is_moderator || false);
      // Permissão granular da Central de Ajuda (super_admin OU moderador OU flag).
      setPodeEditarFaq(response.data.is_super_admin || response.data.is_moderator || response.data.pode_editar_faq || false);
    } catch (error) {
      console.error('Erro ao verificar status de admin:', error);
      setIsSuperAdmin(false);
      setIsModerator(false);
      setPodeEditarFaq(false);
      // Never grant access on errors — keep previous data if it exists,
      // otherwise set null (blocks access). Backend is the source of truth.
      setSubscriptionData(prev => prev);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAdminStatus();
  }, [fetchAdminStatus]);

  // Re-fetch when user logs in (token changes)
  useEffect(() => {
    const handleAuthChange = () => {
      setIsLoading(true);
      fetchAdminStatus();
    };
    window.addEventListener('auth-login', handleAuthChange);
    return () => window.removeEventListener('auth-login', handleAuthChange);
  }, [fetchAdminStatus]);

  // Derived: is the plan active?
  // Super admins always have access (backend returns is_super_admin: true)
  // Regular users: has_subscription must be true
  const isPlanActive = isSuperAdmin || subscriptionData?.is_super_admin === true || subscriptionData?.has_subscription === true;

  // Derived: is the plan expired? (loading finished AND plan not active)
  const isPlanExpired = !isLoading && !isPlanActive;

  // Refresh function for re-checking subscription after renewal
  const refreshSubscription = useCallback(() => {
    setIsLoading(true);
    fetchAdminStatus();
  }, [fetchAdminStatus]);

  return (
    <AdminContext.Provider value={{
      isSuperAdmin,
      isModerator,
      podeEditarFaq,
      isLoading,
      subscriptionData,
      isPlanActive,
      isPlanExpired,
      refreshSubscription,
    }}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
